// Composant réutilisable : sélection / création d'un tiers pour la saisie.
// Deux boutons CLIENT | FOURNISSEUR puis recherche par nom dans la liste,
// avec option de création si le tiers n'existe pas.
import React, { useEffect, useState } from "react";
import api, { formatErreur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Plus, Check, UserPlus, Loader2 } from "lucide-react";

const RACINES = { client: "411", fournisseur: "401" };

export function SelecteurTiers({ onSelect, typeFixe = null, testId = "selecteur-tiers", valeurInitiale = null }) {
  const [type, setType] = useState(typeFixe); // "client" | "fournisseur"
  const [terme, setTerme] = useState("");
  const [resultats, setResultats] = useState([]);
  const [selection, setSelection] = useState(valeurInitiale);
  const [creation, setCreation] = useState(false);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState("");

  useEffect(() => {
    if (!type) return;
    const minuteur = setTimeout(async () => {
      try {
        const { data } = await api.get("/tiers", {
          params: { type, search: terme, inclure_inactifs: false },
        });
        setResultats(data || []);
      } catch {
        setResultats([]);
      }
    }, 200);
    return () => clearTimeout(minuteur);
  }, [type, terme]);

  const changerType = (t) => {
    setType(t);
    setTerme("");
    setSelection(null);
    setCreation(false);
    setErreur("");
  };

  const choisir = (tiers) => {
    setSelection(tiers);
    onSelect?.(tiers);
  };

  const creerTiers = async () => {
    setErreur("");
    if (!terme.trim()) {
      setErreur("Veuillez saisir un nom.");
      return;
    }
    setChargement(true);
    try {
      const { data } = await api.post("/tiers", { type, nom: terme });
      choisir(data);
      setCreation(false);
      setResultats((r) => [data, ...r]);
    } catch (e) {
      setErreur(formatErreur(e.response?.data?.detail));
    } finally {
      setChargement(false);
    }
  };

  const comptePrevisualise = `${RACINES[type] || ""}${terme.trim().toUpperCase()}`;

  return (
    <div className="space-y-3" data-testid={testId}>
      {/* Choix du type (masqué si le type est imposé) */}
      {!typeFixe && (
      <div className="flex gap-2">
        <button
          type="button"
          data-testid={`${testId}-type-client`}
          onClick={() => changerType("client")}
          className={`flex-1 h-10 rounded-md text-sm font-semibold transition-colors ${
            type === "client" ? "bg-[#1D3D3D] text-white" : "bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20"
          }`}
        >
          CLIENT
        </button>
        <button
          type="button"
          data-testid={`${testId}-type-fournisseur`}
          onClick={() => changerType("fournisseur")}
          className={`flex-1 h-10 rounded-md text-sm font-semibold transition-colors ${
            type === "fournisseur" ? "bg-[#1D3D3D] text-white" : "bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20"
          }`}
        >
          FOURNISSEUR
        </button>
      </div>
      )}

      {type && (
        <>
          {/* Recherche */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A8A8A]" />
            <Input
              data-testid={`${testId}-recherche`}
              value={terme}
              onChange={(e) => { setTerme(e.target.value); setSelection(null); setCreation(false); }}
              placeholder={`Rechercher un ${type}…`}
              className="pl-10 bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]"
            />
          </div>

          {/* Résultats */}
          {!selection && resultats.length > 0 && (
            <div className="rounded-md border border-[#4A8A8A]/20 divide-y divide-[#4A8A8A]/10 max-h-56 overflow-y-auto">
              {resultats.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`tiers-option-${t.id}`}
                  onClick={() => choisir(t)}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-left hover:bg-[#E8F4F4]/60"
                >
                  <span className="text-[#1D3D3D] font-medium">{t.nom}</span>
                  <span className="text-[#4A8A8A] text-xs font-mono">{t.compte}</span>
                </button>
              ))}
            </div>
          )}

          {/* Aucun résultat -> proposer la création */}
          {!selection && terme.trim() && resultats.length === 0 && !creation && (
            <button
              type="button"
              data-testid={`${testId}-creer`}
              onClick={() => setCreation(true)}
              className="w-full flex items-center justify-center gap-2 h-10 rounded-md border border-dashed border-[#4A8A8A]/50 text-sm font-medium text-[#1D3D3D] hover:bg-[#E8F4F4]/60"
            >
              <UserPlus className="h-4 w-4" /> Créer « {terme.trim().toUpperCase()} »
            </button>
          )}

          {/* Confirmation de création */}
          {creation && (
            <div className="rounded-md bg-[#E8F4F4] border border-[#4A8A8A]/20 p-3 space-y-2">
              {erreur && <p className="text-sm text-red-600">{erreur}</p>}
              <p className="text-sm text-[#1D3D3D]">
                Nouveau tiers : <span className="font-semibold">{terme.trim().toUpperCase()}</span>
              </p>
              <p className="text-xs text-[#4A8A8A] font-mono">Compte : {comptePrevisualise}</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  data-testid={`${testId}-confirmer-creation`}
                  onClick={creerTiers}
                  disabled={chargement}
                  className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white h-9 text-sm"
                >
                  {chargement ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Créer</>}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setCreation(false)} className="h-9 text-sm">
                  Annuler
                </Button>
              </div>
            </div>
          )}

          {/* Tiers sélectionné -> confirmation visuelle du compte */}
          {selection && (
            <div
              data-testid={`${testId}-confirmation`}
              className="flex items-center justify-between rounded-md bg-[#1D3D3D] text-white px-3 py-2.5"
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <Check className="h-4 w-4 text-[#4A8A8A]" /> {selection.nom}
              </span>
              <span className="text-xs font-mono text-white/80">{selection.compte}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SelecteurTiers;
